import React from "react";
import { Globe2, X } from "lucide-react";
import { toast } from "sonner";

import { PageTemplate } from "@components/pageTemplate/PageTemplate";
import { Badge } from "@components/ui/badge";
import { Button } from "@components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@components/ui/card";
import { Input } from "@components/ui/input";
import { Label } from "@components/ui/label";
import { SelectionCombobox } from "@components/ui/selection-combobox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import { Skeleton } from "@components/ui/skeleton";
import { Textarea } from "@components/ui/textarea";
import {
  useCreateLocalisationFileMutation,
  useLocalisationAdminOverviewQuery,
  useSetLocalisationLocaleEnabledMutation,
  useUpdateLocalisationFileMutation,
} from "generated/hooks";
import { parseServerError } from "~/util/parseError";

import { LOCALISATION_LOCALE_CODES } from "./localisationLocaleCodes";

const BASE_LOCALE = "en-GB";
const SETUP_STATUS_POLL_INTERVAL_MS = 2000;

const languageDisplayNames =
  typeof Intl !== "undefined" && "DisplayNames" in Intl
    ? new Intl.DisplayNames(["en"], { type: "language" })
    : null;
const regionDisplayNames =
  typeof Intl !== "undefined" && "DisplayNames" in Intl
    ? new Intl.DisplayNames(["en"], { type: "region" })
    : null;

const getLocaleLabel = (locale: string) => {
  const [languageCode = locale] = locale.split("-");
  const languageName = languageDisplayNames?.of(languageCode) ?? languageCode;
  const regionCode = locale.split("-").slice(1).find(Boolean);
  const regionName = regionCode
    ? regionDisplayNames?.of(regionCode) ?? regionCode
    : null;

  return regionName
    ? `${locale} - ${languageName} (${regionName})`
    : `${locale} - ${languageName}`;
};

const tryParseJson = (value: string) => {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return {
      parsed: null as unknown,
      error: null,
      formatted: "",
    };
  }

  try {
    const parsed = JSON.parse(trimmed) as unknown;
    const validationError = validateLocalisationJsonShape(parsed);
    if (validationError) {
      return {
        parsed: null as unknown,
        error: validationError,
        formatted: "",
      };
    }

    return {
      parsed,
      error: null,
      formatted: JSON.stringify(parsed, null, 2),
    };
  } catch (error) {
    return {
      parsed: null as unknown,
      error:
        error instanceof Error ? error.message : "Invalid JSON.",
      formatted: "",
    };
  }
};

const validateLocalisationJsonShape = (
  value: unknown,
  prefix: string | null = null
): string | null => {
  if (!isPlainObject(value)) {
    return prefix === null
      ? "Localisation JSON must have an object at the root."
      : `Localisation key \`${prefix}\` must contain a string value or nested object.`;
  }

  for (const [rawKey, nestedValue] of Object.entries(value)) {
    if (rawKey.trim().length === 0) {
      return "Localisation key segments must not be blank.";
    }

    if (rawKey !== rawKey.trim()) {
      return "Localisation key segments must not have leading or trailing whitespace.";
    }

    if (rawKey.includes(".")) {
      return "Localisation key segments must not contain '.'.";
    }

    const nextPath = prefix ? `${prefix}.${rawKey}` : rawKey;
    if (typeof nestedValue === "string") {
      continue;
    }

    if (Array.isArray(nestedValue)) {
      return `Localisation key \`${nextPath}\` contains an array. Arrays are not supported.`;
    }

    if (isPlainObject(nestedValue)) {
      if (Object.keys(nestedValue).length === 0) {
        return `Localisation key \`${nextPath}\` contains an empty object. Empty objects are not supported.`;
      }

      const nestedError = validateLocalisationJsonShape(nestedValue, nextPath);
      if (nestedError) {
        return nestedError;
      }

      continue;
    }

    return `Localisation key \`${nextPath}\` must contain a string value or nested object.`;
  }

  return null;
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isActiveSetupStatus = (status?: string | null) =>
  status === "queued" || status === "running";

const getSetupStatusLabel = (status?: string | null) => {
  switch (status) {
    case "queued":
      return "Backfill queued";
    case "running":
      return "Backfill running";
    case "completed":
      return "Backfill completed";
    case "failed":
      return "Backfill failed";
    default:
      return null;
  }
};

const getSetupStatusVariant = (status?: string | null) => {
  switch (status) {
    case "failed":
      return "destructive" as const;
    case "completed":
      return "secondary" as const;
    case "queued":
    case "running":
      return "default" as const;
    default:
      return "outline" as const;
  }
};

const formatSetupUpdatedAt = (value?: string | null) => {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return new Intl.DateTimeFormat("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(parsed);
};

const renderJsonPreview = (formattedJson: string) => {
  const tokenPattern =
    /"(?:\\.|[^"\\])*"(?=\s*:)|"(?:\\.|[^"\\])*"|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?|\btrue\b|\bfalse\b|\bnull\b|[{}[\]:,]/g;

  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;

  for (const match of formattedJson.matchAll(tokenPattern)) {
    const token = match[0];
    const index = match.index ?? 0;

    if (index > lastIndex) {
      nodes.push(
        <span key={`text-${lastIndex}`}>
          {formattedJson.slice(lastIndex, index)}
        </span>
      );
    }

    let className = "text-foreground";
    if (/^"(?:\\.|[^"\\])*"$/.test(token)) {
      className = token.endsWith('"') && formattedJson[index + token.length] === ":"
        ? "text-sky-600 dark:text-sky-400"
        : "text-emerald-600 dark:text-emerald-400";
    } else if (/^-?\d/.test(token)) {
      className = "text-amber-600 dark:text-amber-400";
    } else if (token === "true" || token === "false" || token === "null") {
      className = "text-violet-600 dark:text-violet-400";
    } else if (/^[{}[\]:,]$/.test(token)) {
      className = "text-muted-foreground";
    }

    nodes.push(
      <span key={`token-${index}`} className={className}>
        {token}
      </span>
    );
    lastIndex = index + token.length;
  }

  if (lastIndex < formattedJson.length) {
    nodes.push(
      <span key={`text-${lastIndex}`}>
        {formattedJson.slice(lastIndex)}
      </span>
    );
  }

  return nodes;
};

export const getCreateFileSuccessMessage = (fileName: string) =>
  `Created localisation file ${fileName}. Any enabled-locale backfill will run in the background.`;

export const getUpdateFileSuccessMessage = (
  fileName: string,
  hasJsonContent: boolean
) =>
  hasJsonContent
    ? `Updated localisation file ${fileName}. Any required translation backfill will run in the background.`
    : `Updated localisation file ${fileName}.`;

export const getSetLocaleEnabledSuccessMessage = (
  locale: string,
  isEnabled: boolean
) =>
  isEnabled
    ? `Enabled ${locale} for all localisation files. Translation backfill will run in the background.`
    : `Disabled ${locale} for all localisation files.`;

const SuperAdminLocalisationPage = () => {
  const [createFileName, setCreateFileName] = React.useState("");
  const [createJsonContent, setCreateJsonContent] = React.useState("");
  const [createTranslationContext, setCreateTranslationContext] = React.useState("");
  const [updateFileName, setUpdateFileName] = React.useState<string | null>(
    null
  );
  const [updateJsonContent, setUpdateJsonContent] = React.useState("");
  const [updateTranslationContext, setUpdateTranslationContext] = React.useState("");
  const [isEditingUpdateTranslationContext, setIsEditingUpdateTranslationContext] =
    React.useState(false);
  const [pendingLocale, setPendingLocale] = React.useState("");

  const {
    data,
    loading,
    error,
    refetch: refetchOverview,
    startPolling,
    stopPolling,
  } = useLocalisationAdminOverviewQuery();
  const [createLocalisationFile, { loading: creatingFile }] =
    useCreateLocalisationFileMutation();
  const [updateLocalisationFile, { loading: updatingFile }] =
    useUpdateLocalisationFileMutation();
  const [setLocalisationLocaleEnabled, { loading: updatingLocale }] =
    useSetLocalisationLocaleEnabledMutation();

  const files = data?.localisationAdminOverview.files ?? [];
  const enabledLocales = data?.localisationAdminOverview.enabledLocales ?? [];
  const enabledLocaleSummaries =
    data?.localisationAdminOverview.enabledLocaleSummaries ?? [];
  const enabledLocaleSummariesByCode = React.useMemo(
    () =>
      new Map(
        enabledLocaleSummaries.map((localeSummary) => [
          localeSummary.locale,
          localeSummary,
        ])
      ),
    [enabledLocaleSummaries]
  );
  const effectiveUpdateFileName = updateFileName ?? files[0]?.name ?? null;
  const selectedUpdateFile =
    files.find((file) => file.name === effectiveUpdateFileName) ?? null;
  const availableLocalesToAdd = React.useMemo(
    () =>
      LOCALISATION_LOCALE_CODES.filter(
        (locale) =>
          locale !== BASE_LOCALE && !enabledLocales.includes(locale)
      ).map((locale) => ({
        value: locale,
        label: getLocaleLabel(locale),
      })),
    [enabledLocales]
  );
  const selectedPendingLocaleLabel =
    availableLocalesToAdd.find((option) => option.value === pendingLocale)
      ?.label ?? "Search locales";
  const createJsonState = React.useMemo(
    () => tryParseJson(createJsonContent),
    [createJsonContent]
  );
  const updateJsonState = React.useMemo(
    () => tryParseJson(updateJsonContent),
    [updateJsonContent]
  );
  const hasCreateJsonContent = createJsonContent.trim().length > 0;
  const canSubmitCreate =
    !creatingFile &&
    createFileName.trim().length > 0 &&
    hasCreateJsonContent &&
    !createJsonState.error;
  const normalizedUpdateTranslationContext =
    updateTranslationContext.trim().length > 0
      ? updateTranslationContext.trim()
      : null;
  const originalUpdateTranslationContext =
    selectedUpdateFile?.translationContext ?? null;
  const hasUpdateJsonContent = updateJsonContent.trim().length > 0;
  const hasUpdateContextChanged =
    normalizedUpdateTranslationContext !== originalUpdateTranslationContext;
  const hasActiveSetupTasks = React.useMemo(
    () =>
      files.some((file) => isActiveSetupStatus(file.setupStatus)) ||
      enabledLocaleSummaries.some((localeSummary) =>
        isActiveSetupStatus(localeSummary.setupStatus)
      ),
    [enabledLocaleSummaries, files]
  );
  const canSubmitUpdate =
    !updatingFile &&
    files.length > 0 &&
    Boolean(effectiveUpdateFileName) &&
    ((hasUpdateJsonContent && !updateJsonState.error) || hasUpdateContextChanged);

  const handleError = (label: string, error: unknown) => {
    toast.error(`${label}: ${parseServerError(error).message}`);
  };

  React.useEffect(() => {
    if (hasActiveSetupTasks) {
      startPolling?.(SETUP_STATUS_POLL_INTERVAL_MS);
      return () => {
        stopPolling?.();
      };
    }

    stopPolling?.();
  }, [hasActiveSetupTasks, startPolling, stopPolling]);

  const handleCreateFile = async () => {
    if (!canSubmitCreate) {
      return;
    }

    try {
      const nextFileName = createFileName.trim();

      await createLocalisationFile({
        variables: {
          input: {
            name: nextFileName,
            jsonContent: createJsonState.formatted,
            translationContext:
              createTranslationContext.trim().length > 0
                ? createTranslationContext.trim()
                : null,
          },
        },
      });

      await refetchOverview();
      setCreateFileName("");
      setCreateJsonContent("");
      setCreateTranslationContext("");
      toast.success(getCreateFileSuccessMessage(nextFileName));
    } catch (error) {
      handleError("Failed to create localisation file", error);
    }
  };

  const handleUpdateFile = async () => {
    if (!effectiveUpdateFileName || !canSubmitUpdate) {
      return;
    }

    try {
      const nextFileName = effectiveUpdateFileName;

      await updateLocalisationFile({
        variables: {
          input: {
            name: nextFileName,
            jsonContent: hasUpdateJsonContent ? updateJsonState.formatted : null,
            translationContext: normalizedUpdateTranslationContext,
          },
        },
      });

      await refetchOverview();
      setUpdateJsonContent("");
      setUpdateTranslationContext(normalizedUpdateTranslationContext ?? "");
      setIsEditingUpdateTranslationContext(false);
      toast.success(
        getUpdateFileSuccessMessage(nextFileName, hasUpdateJsonContent)
      );
    } catch (error) {
      handleError("Failed to update localisation file", error);
    }
  };

  React.useEffect(() => {
    const nextContext = selectedUpdateFile?.translationContext ?? "";
    if (
      isEditingUpdateTranslationContext &&
      updateTranslationContext !== nextContext
    ) {
      return;
    }

    setIsEditingUpdateTranslationContext(false);
    setUpdateTranslationContext(selectedUpdateFile?.translationContext ?? "");
  }, [
    isEditingUpdateTranslationContext,
    selectedUpdateFile?.name,
    selectedUpdateFile?.translationContext,
    updateTranslationContext,
  ]);

  const handleSetLocaleEnabled = async (locale: string, isEnabled: boolean) => {
    try {
      await setLocalisationLocaleEnabled({
        variables: {
          input: {
            locale,
            isEnabled,
          },
        },
      });

      await refetchOverview();
      if (isEnabled) {
        setPendingLocale("");
        toast.success(getSetLocaleEnabledSuccessMessage(locale, true));
      } else {
        toast.success(getSetLocaleEnabledSuccessMessage(locale, false));
      }
    } catch (error) {
      handleError(
        isEnabled ? "Failed to enable locale" : "Failed to disable locale",
        error
      );
    }
  };

  return (
    <PageTemplate
      title="Localisation"
      subtitle="Manage base files and system-wide locales."
      activeItem="Super admin"
      activeSubItem="Localisation setup"
      browserTitle="Localisation"
    >
      <div className="space-y-6">
        {error ? (
          <Card className="border-destructive/40">
            <CardHeader>
              <CardTitle>Unable to load localisation management</CardTitle>
              <CardDescription>{error.message}</CardDescription>
            </CardHeader>
          </Card>
        ) : null}

        <div className="grid gap-6 xl:grid-cols-3">
          <Card className="xl:col-span-1">
            <CardHeader>
              <CardTitle>Localisation Files</CardTitle>
              <CardDescription>
                Base en-GB files currently configured.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {loading ? (
                Array.from({ length: 3 }).map((_, index) => (
                  <Skeleton key={index} className="h-20 rounded-md" />
                ))
              ) : files.length === 0 ? (
                <div className="rounded-md border border-dashed border-border/70 px-4 py-6 text-sm text-muted-foreground">
                  No localisation files exist yet.
                </div>
              ) : (
                files.map((file) => (
                  <div
                    key={file.name}
                    className="rounded-md border border-border/70 px-4 py-4"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="font-medium">{file.name}</div>
                        <div className="mt-1 text-sm text-muted-foreground">
                          {file.baseEntryCount} base strings
                        </div>
                        {file.setupStatus ? (
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            <Badge variant={getSetupStatusVariant(file.setupStatus)}>
                              {getSetupStatusLabel(file.setupStatus)}
                            </Badge>
                            {file.setupUpdatedAt ? (
                              <span className="text-xs text-muted-foreground">
                                {formatSetupUpdatedAt(file.setupUpdatedAt)}
                              </span>
                            ) : null}
                          </div>
                        ) : null}
                        {file.translationContext ? (
                          <div className="mt-2 text-xs text-muted-foreground">
                            {file.translationContext}
                          </div>
                        ) : null}
                        {file.setupFailureMessage ? (
                          <div className="mt-2 text-xs text-destructive">
                            {file.setupFailureMessage}
                          </div>
                        ) : null}
                      </div>
                      <Badge variant="secondary">
                        {file.targetLocaleCount} locales
                      </Badge>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          <Card className="xl:col-span-1">
            <CardHeader>
              <CardTitle>Create File</CardTitle>
              <CardDescription>
                Paste en-GB JSON to create a new localisation file.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="create-localisation-name">File name</Label>
                <Input
                  id="create-localisation-name"
                  value={createFileName}
                  onChange={(event) => setCreateFileName(event.target.value)}
                  placeholder="mobile"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-localisation-json">JSON</Label>
                <Textarea
                  id="create-localisation-json"
                  value={createJsonContent}
                  onChange={(event) => setCreateJsonContent(event.target.value)}
                  placeholder={`{\n  "app": {\n    "title": "Example App"\n  }\n}`}
                  className="min-h-56 font-mono text-sm"
                  aria-invalid={createJsonState.error ? true : undefined}
                />
                <div
                  className={
                    createJsonState.error || !hasCreateJsonContent
                      ? "text-xs text-destructive"
                      : "text-xs text-muted-foreground"
                  }
                >
                  {!hasCreateJsonContent
                    ? "JSON is required."
                    : createJsonState.error ?? "Valid en-GB JSON."}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-localisation-context">
                  Translation context
                </Label>
                <Textarea
                  id="create-localisation-context"
                  value={createTranslationContext}
                  onChange={(event) =>
                    setCreateTranslationContext(event.target.value)
                  }
                  placeholder="For example: This is a mobile agtech application for measuring soil health."
                  className="min-h-24"
                />
                <div className="text-xs text-muted-foreground">
                  Sent with every AI translation request for this file.
                </div>
              </div>
              {createJsonState.formatted ? (
                <div className="space-y-2">
                  <Label>Preview</Label>
                  <pre className="max-h-56 overflow-auto rounded-md border border-border/70 bg-muted/20 p-3 font-mono text-sm whitespace-pre-wrap">
                    {renderJsonPreview(createJsonState.formatted)}
                  </pre>
                </div>
              ) : null}
              <div className="text-xs text-muted-foreground">
                Paste the full en-GB JSON object. Submission is blocked until the
                JSON parses successfully.
              </div>
              <Button
                type="button"
                onClick={() => void handleCreateFile()}
                disabled={
                  !canSubmitCreate
                }
                className="w-full"
              >
                Create localisation file
              </Button>
            </CardContent>
          </Card>

          <Card className="xl:col-span-1">
            <CardHeader>
              <CardTitle>Update File</CardTitle>
              <CardDescription>
                Paste updated en-GB JSON for an existing localisation file.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="update-localisation-target">Existing file</Label>
                <Select
                  value={effectiveUpdateFileName ?? undefined}
                  onValueChange={(value) => {
                    setIsEditingUpdateTranslationContext(false);
                    setUpdateFileName(value);
                  }}
                  disabled={files.length === 0}
                >
                  <SelectTrigger id="update-localisation-target">
                    <SelectValue placeholder="Select file" />
                  </SelectTrigger>
                  <SelectContent>
                    {files.map((file) => (
                      <SelectItem key={file.name} value={file.name}>
                        {file.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="update-localisation-json">JSON</Label>
                <Textarea
                  id="update-localisation-json"
                  value={updateJsonContent}
                  onChange={(event) => setUpdateJsonContent(event.target.value)}
                  placeholder={`{\n  "app": {\n    "title": "Example App"\n  }\n}`}
                  disabled={files.length === 0}
                  className="min-h-56 font-mono text-sm"
                  aria-invalid={updateJsonState.error ? true : undefined}
                />
                <div
                  className={
                    updateJsonState.error
                      ? "text-xs text-destructive"
                      : "text-xs text-muted-foreground"
                  }
                >
                  {updateJsonState.error ??
                    (hasUpdateJsonContent
                      ? "Valid en-GB JSON."
                      : "Leave blank to update only the translation context.")}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="update-localisation-context">
                  Translation context
                </Label>
                <Textarea
                  id="update-localisation-context"
                  value={updateTranslationContext}
                  onChange={(event) => {
                    setIsEditingUpdateTranslationContext(true);
                    setUpdateTranslationContext(event.target.value);
                  }}
                  placeholder="For example: This is a mobile agtech application for measuring soil health."
                  disabled={files.length === 0}
                  className="min-h-24"
                />
                <div className="text-xs text-muted-foreground">
                  Sent with every AI translation request for the selected file.
                </div>
              </div>
              {updateJsonState.formatted ? (
                <div className="space-y-2">
                  <Label>Preview</Label>
                  <pre className="max-h-56 overflow-auto rounded-md border border-border/70 bg-muted/20 p-3 font-mono text-sm whitespace-pre-wrap">
                    {renderJsonPreview(updateJsonState.formatted)}
                  </pre>
                </div>
              ) : null}
              <div className="text-xs text-muted-foreground">
                Paste replacement en-GB JSON to change keys, or leave JSON blank
                to update only the translation context.
              </div>
              <Button
                type="button"
                onClick={() => void handleUpdateFile()}
                disabled={!canSubmitUpdate}
                className="w-full"
              >
                Update localisation file
              </Button>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Enabled Locales</CardTitle>
            <CardDescription>
              Enable or disable target locales for every localisation file.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <Badge variant="secondary">{getLocaleLabel(BASE_LOCALE)}</Badge>
              {enabledLocales.map((locale) => {
                const summary = enabledLocaleSummariesByCode.get(locale);

                return (
                  <div
                    key={locale}
                    className="inline-flex items-center gap-3 rounded-md border border-border/70 px-3 py-2"
                  >
                    <div className="space-y-1">
                      <div className="text-sm">{getLocaleLabel(locale)}</div>
                      {summary?.setupStatus ? (
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant={getSetupStatusVariant(summary.setupStatus)}>
                            {getSetupStatusLabel(summary.setupStatus)}
                          </Badge>
                          {summary.setupUpdatedAt ? (
                            <span className="text-xs text-muted-foreground">
                              {formatSetupUpdatedAt(summary.setupUpdatedAt)}
                            </span>
                          ) : null}
                        </div>
                      ) : null}
                      {summary?.setupFailureMessage ? (
                        <div className="text-xs text-destructive">
                          {summary.setupFailureMessage}
                        </div>
                      ) : null}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => void handleSetLocaleEnabled(locale, false)}
                      disabled={updatingLocale}
                      aria-label={`Disable ${locale}`}
                      className="rounded-full"
                    >
                      <X className="size-4" />
                    </Button>
                  </div>
                );
              })}
            </div>

            <div className="grid gap-4 md:grid-cols-[1fr_auto]">
              <div className="space-y-2">
                <Label>Locale code</Label>
                <SelectionCombobox
                  placeholder={selectedPendingLocaleLabel}
                  options={availableLocalesToAdd}
                  onSelect={setPendingLocale}
                  emptyMessage="No locales found"
                  searchPlaceholder="Search locales..."
                  disabled={availableLocalesToAdd.length === 0}
                />
              </div>
              <Button
                type="button"
                onClick={() =>
                  void handleSetLocaleEnabled(pendingLocale.trim(), true)
                }
                disabled={updatingLocale || pendingLocale.trim().length === 0}
                className="self-end"
              >
                <Globe2 className="mr-2 size-4" />
                Enable locale
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </PageTemplate>
  );
};

export default SuperAdminLocalisationPage;
